<template>
  <div
    class="fts-file-upload"
    :class="{'comfort-design': comfortDesign}"
    ref="ftsFileUpload"
    tabindex="0"
  >
    <!-- header -->
    <div class="file-upload__header" v-if="isUploadHeader">
      <!-- uploader -->
      <div class="file-upload-uploader">
        <!-- dropzone -->
        <div class="fts-file-dropzone" :class="{ dragging: isDragging }">
          <file-upload
            :input-id="item.fieldName + uid"
            ref="upload"
            v-model="files"
            :drop="true"
            :size="item.maxFileSize ? item.maxFileSize * 1024 * 1024 : 0"
            :multiple="multiple"
            :post-action="fileUploadURL"
            :data="uploadFormData"
            @input-file="inputFile"
            @input-filter="inputFilter"
          >
            <div>
              <font-awesome-icon
                :icon="['fas', 'cloud-upload-alt']"
                size="lg"
                class="dropzone-icon"
              />
              <span class="dropzone-label">
                Drag & drop {{ uploadLabel }} here or browse
              </span>
            </div>
          </file-upload>
        </div>
        <!-- action -->
        <div class="action" v-if="isImageUpload">
          <div @click="openModal('takePhoto')">
            <div>
              <font-awesome-icon
                :icon="['fas', 'camera']"
                size="lg"
                fixed-width
              />
            </div>
          </div>
          <div @click="openModal('pastePhoto')">
            <div>
              <font-awesome-icon :icon="['fas', 'paste']" size="lg" fixed-width />
            </div>
          </div>
        </div>
      </div>
      <!-- queue -->
      <fts-file 
        class="file-upload-queue"
        :data="queueFiles" 
        cancellable
        @cancelUpload="cancelUpload" 
      />
    </div>
    <!-- preview -->
    <div class="file-upload__content">
      <!-- empty -->
      <div class="empty-item" v-if="isEmpty">
        Upload file to preview.
      </div>
      <!-- uploaded -->
      <fts-file
        class="file-upload-display"
        :prefixURL="`${vectorId}-${item.id}`"
        :data="formData[item.fieldName]"
        :file-type="fileType"
        editable
        downloadable
        @removeFile="removeFile($event)"
        v-else
      />
    </div>
    <!-- upload modal -->
    <fts-client-modal
      :title="modalTitle"
      class="file-upload-modal"
      @closed="resetModal"
      ref="ftsClientModal"
    >
      <!-- header:icon -->
      <font-awesome-icon
        slot="icon"
        :icon="['fas', 'camera']"
        fixed-width
        v-if="selected === 'takePhoto'"
      />
      <font-awesome-icon
        slot="icon"
        :icon="['fas', 'paste']"
        fixed-width
        v-if="selected === 'pastePhoto'"
      />
      <!-- body -->
      <template #body>
        <div class="file-upload-modal-camera" v-if="selected == 'takePhoto'">
          <canvas
            width="1280"
            height="960"
            v-show="photoCaptured"
            ref="canvas"
          />
          <video
            width="1280"
            v-show="!photoCaptured"
            ref="video"
          />
          <div class="camera-action-bar">
            <template v-if="photoCaptured">
              <fts-btn
                outline
                color="primary"
                text-transform-init
                @click="photoCaptured = false"
              >
                Retake Photo
              </fts-btn>

              <fts-btn
                color="primary"
                text-transform-init
                box-shadow-none
                @click="saveCaptureImage"
              >
                Save
              </fts-btn>
            </template>
            <fts-btn
              outline 
              color="primary"
              text-transform-init
              @click="onCapture"
              v-else
            >
              Snap Photo
            </fts-btn>
          </div>
        </div>
        <div class="file-upload-modal-paste" v-else-if="selected == 'pastePhoto'">
          Press <kbd>Ctrl</kbd> + <kbd>V</kbd> to paste
        </div>
        <div v-else>
          <h2>Something went wrong.</h2>
        </div>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn text @click="closeModal">
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>
      </template>
    </fts-client-modal>
  </div>
</template>

<script>
import VueUploadComponent from "../vue-upload-component/index";
import ImageCompressor from "compressorjs";

import webAppConfig from "../commons/webApp.config";
import commons from "../commons/commons";

import FTSClientModal from "./FTSClientModal";
import FTSFile from "./file/FTSFile";
import FTSBtn from "./FTSBtn";

export default {
  props: {
    vectorId: [String, Number],
    item: Object,
    formData: Object,
    additionalFormData: Object,
    comfortDesign: Boolean,
  },
  components: {
    FileUpload: VueUploadComponent,
    "fts-client-modal": FTSClientModal,
    "fts-file": FTSFile,
    "fts-btn": FTSBtn,
  },
  data: () => ({
    uid: null,
    files: [],
    isDragging: false,
    selected: null,
    photoCaptured: false,
    stream: false,
  }),
  methods: {
    offVideoStream() {
      if (this.stream && this.stream.active) {
        this.stream.getTracks().map(x => {
          x.stop();
        });
        this.stream = false;
      }
    },
    async openModal(selected) {
      this.selected = selected;
      this.$refs.ftsClientModal.show();
      if (this.selected == "takePhoto") {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        this.stream = stream;
        this.$nextTick(() => {
          let video = this.$refs.video;
          if (video) {
            video.srcObject = stream;
            video.play();
          } else {
            this.offVideoStream();
          }
        });
      } else if (this.selected == "pastePhoto") {
        window.addEventListener("paste", this.onPaste);
      }
    },
    async closeModal() {
      this.$refs.ftsClientModal.close();
    },
    resetModal() {
      if (this.selected == "pastePhoto") {
        window.removeEventListener("paste", this.onPaste);
      }
      this.offVideoStream();
      this.selected = null;
      this.photoCaptured = false;
    },
    uploadDataUrl(dataURI) {
      let vm = this;

      var byteString = atob(dataURI.split(",")[1]);
      var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
      var ab = new ArrayBuffer(byteString.length);
      var ia = new Uint8Array(ab);
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      let blob = new Blob([ab], { type: mimeString });
      // Without name, image wouldn't save
      blob.name = "image.png";
      vm.$refs.upload.add(blob, { active: false });
    },
    onCapture() {
      this.photoCaptured = true;
      const canvas = this.$refs.canvas.getContext("2d");
      const video = this.$refs.video;
      canvas.drawImage(video, 0, 0, 1280, 960);
    },
    saveCaptureImage() {
      this.offVideoStream();
      if (this.photoCaptured) {
        this.uploadDataUrl(this.$refs.canvas.toDataURL());
      }
      this.closeModal();
    },
    onPaste() {
      this.closeModal();
      var items = (
        event.clipboardData || 
        event.originalEvent.clipboardData
      ).items;

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (item.kind === "file") {
          var file = item.getAsFile();
          this.$refs.upload.add(file, { active: false });
        }
      }
    },
    /**
     * Has changed
     * @param  Object|undefined   newFile   只读
     * @param  Object|undefined   oldFile   只读
     * @return undefined
     */
    inputFile(newFile, oldFile) {
      if (newFile && oldFile) {
        /* before send */
        if (newFile.active && !oldFile.active) {
          if (newFile.name === "image.png" || newFile.name === "unknown")
            newFile.name = `${commons.nanoID()}.png`;
          else
            newFile.name = newFile.name.replaceAll("/", "_");
        }
        /* on progress */
        if (newFile.progress !== oldFile.progress) {
          // progress
        }
        /* on error */
        if (newFile.error && !oldFile.error) {
          if (newFile.xhr) {
            /* xhr status */
            console.log("status", newFile.xhr.status);
            /* xhr error handling */
            if (newFile.xhr.status === 413)
              this.$toasted.error('Upload failed. Request entity too large.');
            else if (newFile.xhr.status === 500)
              this.$toasted.error('Upload failed. Something went wrong.');
          } else if (newFile.error == "size") {
            /* size error handling */
            this.$toasted.error(`Upload failed. File size can not exceed ${this.item.maxFileSize} MB.`);
          }
        }
        /* on success */
        if (newFile.success && !oldFile.success) {
          this.$emit("fileUploaded", newFile.response);
          this.$emit("input", newFile.response);

          if (this.multiple) {
            if (this.formData[this.item.fieldName] == null)
              this.$set(this.formData, this.item.fieldName, []);

            this.formData[this.item.fieldName].push({
              path: newFile.response.url,
              name: newFile.response.filename,
              size: newFile.response.size,
            });
          } else { 
            this.$set(this.formData, this.item.fieldName, {
              path: newFile.response.url,
              name: newFile.response.filename,
              size: newFile.response.size,
            });
          }
        }
        /* remove file from queue */
        if (!newFile.active && oldFile.active) {
          this.removeQueueFile(newFile);
        }
      }
      /* remove */
      if (!newFile && oldFile) {
        if (oldFile.success && oldFile.response.id) {
          // delete
        }
      }
      /* automatically activate upload */
      if (
        Boolean(newFile) !== Boolean(oldFile) ||
        oldFile.error !== newFile.error
      ) {
        if (!this.$refs.upload.active) {
          this.$refs.upload.active = true;
        }
      }
    },
    /**
     * Pretreatment
     * @param  Object|undefined   newFile   读写
     * @param  Object|undefined   oldFile   只读
     * @param  Function           prevent   阻止回调
     * @return undefined
     */
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (this.fileType === "image") {
          // 过滤不是图片后缀的文件
          if (!/\.(jpeg|jpe|jpg|svg|gif|png|webp|bmp|)$/i.test(newFile.name)) {
            this.$toasted.error(
              `Upload failed. File only accept jpeg, jpe, jpg, svg, gif, png, webp, or bmp format.`
            );
            return prevent();
          }
        }
        // Automatic compression
        // 自动压缩
        if (
          newFile.file &&
          newFile.type.substr(0, 6) === "image/" &&
          this.autoCompress > 0 &&
          this.autoCompress < newFile.size &&
          this.isImageUpload
        ) {
          let vm = this;

          newFile.error = "compressing";
          const imageCompressor = new ImageCompressor(newFile.file, {
            maxWidth: 1200,
            maxHeight: 1200,
            success(file) {
              vm.$refs.upload.update(newFile, {
                error: "",
                file,
                size: file.size,
                type: file.type,
              });
            },
            error(err) {
              vm.$refs.upload.update(newFile, {
                error: err.message || "compress",
              });
            },
          });
        }
      }

      if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
        if (newFile.file != null) {
          // Create a blob field
          // 创建 blob 字段
          newFile.blob = "";
          let URL = window.URL || window.webkitURL;
          if (URL && URL.createObjectURL) {
            newFile.blob = URL.createObjectURL(newFile.file);
          }

          newFile.thumb = "";
          if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
            newFile.thumb = newFile.blob;
          }
        } else {
          var reader = new FileReader();
          reader.onload = function (event) {
            newFile.blob = event.target.result;
            newFile.thumb = "";
            if (newFile.blob && newFile.type.substr(0, 6) === "image/") {
              newFile.thumb = newFile.blob;
            }
          }; // data url!
          reader.readAsDataURL(newFile);
        }
        // Thumbnails
        // 缩略图
      }
    },
    removeFile(file) {
      if (this.multiple && Array.isArray(this.formData[this.item.fieldName])) {
        let tableData = this.formData[this.item.fieldName];
        tableData.splice(tableData.indexOf(file), 1);
      } else {
        this.formData[this.item.fieldName] = null;
      }

      this.$emit("input");
    },
    removeQueueFile(file) {
      const fileIndex = this.files.indexOf(file);
      if (fileIndex !== -1)
        this.files.splice(fileIndex, 1);
    },
    cancelUpload(file) {
      if (file.active)
        this.$refs.upload.active = false;
      else
        this.removeQueueFile(file);
    },
  },
  computed: {
    fileUploadURL() {
      return webAppConfig.fileUploadURL;
    },
    autoCompress() {
      return 500 * 1024;
    },
    isImageUpload() {
      return (
        this.item.type == 10 ||
        this.item.type == 11 ||
        this.item.input === "imagesUpload" ||
        this.item.input === "imageUpload"
      );
    },
    multiple() {
      return (
        this.item.input === "imagesUpload" ||
        this.item.input === "filesUpload" ||
        this.item.type === 11 ||
        this.item.type === 13
      );
    },
    uploadFormData() {
      if (this.$store) {
        let formData = this.$store.getters.uploadFormData({
          fieldName: this.item.fieldName,
          fieldId: this.item.id,
          formType: this.item.formType,
        });
        if (this.additionalFormData) {
          return {
            ...formData,
            ...this.additionalFormData,
          };
        }
        return formData;
      }
      return {};
    },
    uploadLabel() {
      return this.multiple ? `${this.fileType}(s)` : `${this.fileType}`;
    },
    fileType() {
      return this.item.type === 10 ||
        this.item.type === 11 ||
        this.item.input === "imageUpload" ||
        this.item.input === "imagesUpload"
        ? "image"
        : "file";
    },
    isUploadHeader() {
      return !this.multiple && !this.isEmpty ? false : true;
    },
    isEmpty() {
      return (
        (Array.isArray(this.formData[this.item.fieldName]) &&
          this.formData[this.item.fieldName].length === 0) ||
        this.formData[this.item.fieldName] === {} ||
        this.formData[this.item.fieldName] === null ||
        this.formData[this.item.fieldName] === undefined
      );
    },
    queueFiles() {
      return Array.isArray(this.files) && this.files.filter(x => !x.error);
    },
    modalTitle() {
      switch(this.selected) {
        case "takePhoto":
          return "Take Photo"
        case "pastePhoto":
          return "Paste Photo";
        default:
          return "(undefined)";
      }
    },
  },
  watch: {
    queueFiles(val) {
      this.$emit('onQueue', val && val.length);
    },
  },
  mounted() {
    this.uid = this._uid;
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/vars.scss";

.fts-file-upload {
  &.comfort-design {
    .file-upload__header {
      .file-upload-uploader {
        font-size: $comfortDesignFontSize;
        .fts-file-dropzone {
          border-radius: $comfortDesignBorderRadius;
          .file-uploads {
            padding: 1.5rem;
          }
        }
        .action {
          > div {
            border-radius: $comfortDesignBorderRadius;
          }
        }
      }
    }
    .file-upload__content {
      .empty-item {
        min-height: $comfortDesignHeight;
        padding: .625rem 1rem;
        font-size: $comfortDesignFontSize;
        line-height: $comfort-design-line-height;
      }
    }
  }
  .file-upload__header {
    margin-bottom: .375rem;
    .file-upload-uploader {
      display: flex;
      justify-content: space-evenly;
      font-size: 0.75rem;
      .fts-file-dropzone {
        flex: 3 0 0;
        cursor: pointer;
        border: dashed 1px var(--fts-form-control-border);
        background: var(--fts-form-control-background);
        &.dragging,
        &:hover {
          @include theme() {
            background-color: rgba(theme-get('fts-primary'), .095);
          }
          border-color: var(--fts-primary);
          .dropzone-icon,
          .dropzone-label {
            color: var(--fts-primary);
          }
        }
        .dropzone-icon {
          margin-right: .375rem;
          color: var(--fts-gray-5);
        }
        .dropzone-label {
          color: var(--fts-gray-5);
        }
        .file-uploads {
          width: 100%;
          height: 100%;
          padding: 1rem;
          vertical-align: middle;
          > div {
            cursor: pointer;
          }
        }
      }
      .action {
        display: flex;
        flex: 1 0 0;
        > div {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          margin-left: 4px;
          padding: 1rem;
          border: dashed 1px var(--fts-form-control-border);
          background-color: var(--fts-form-control-background);
          cursor: pointer;
          &:hover {
            @include theme() {
              background-color: rgba(theme-get('fts-primary'), .095);
            }
            border-color: var(--fts-primary);
            svg {
              color: var(--fts-primary);
            }
          }
          svg {
            color: var(--fts-gray-5);
          }
        }
      }
    }
    .file-upload-queue {
      margin-top: .375rem;  
    }
  } 
  .file-upload__content {
    .empty-item {
      @include flex-center-middle;
      min-height: calc(1.5em + 0.75rem + 2px);
      padding: 0.375rem 0.75rem;
      font-size: 0.75rem;
      line-height: 1rem;
      color: var(--fts-gray-5);
    }
  }
  /* modal */
  .file-upload-modal {
    .file-upload-modal-camera {
      > canvas,
      > video {
        width: 100%;
      }
      .camera-action-bar {
        margin-top: 1rem;
        text-align: center;
      }
    }
    .file-upload-modal-paste {
      padding: 1.5rem 0;
      text-align: center;
      color: var(--fts-form-label);
    }
  }
}
</style>